import { shallowEqual } from 'react-redux';
import { useAppSelector } from 'state/store-hooks';
import { locationCashierTheme } from 'utils/helper-functions/get-url-vars';
import { themeAppearanceMode } from '@pano-project/variables/theme-mode';
import { isPanoramicTheme } from '@pano-project/variables/is-panoramic-2-0-theme';
import {
  HeaderButtonsSettings,
  resolveHeaderButtons,
  supportsTabsHeader,
} from '@pano-project/variables/header-buttons';
import {
  embeddedAppearance,
  embeddedHeaderStyle,
  embeddedIntents,
  embeddedLayout,
  embeddedPreselectFirstPaymentMethod,
  embeddedPreselectOnlyAvailableMethod,
  embeddedSessionType,
  embeddedShowBackButton,
  embeddedShowCloseButton,
  embeddedTransactionType,
} from '@pano-project/variables/boot-header-settings';
import { isKnownIntent, mapIntentToTransactionType } from '@pano-project/utils/intentMapping';
import { isPreselectedPaymentMethod } from '@pano-project/variables/preselected-payment-method';

export interface BootHeaderSettings extends HeaderButtonsSettings {
  supportsTabs: boolean;
  headerStyle: string | null;
  layout: string | null;
  appearanceMode: string;
  transactionType: string;
  sessionType: string | null;
  isPreselectedPM: boolean;
  withPending: boolean;
  /**
   * The ordered intents the host page booted with, until the session reply
   * arrives; null afterwards (and when the host gave no usable list). Only the
   * start-up header reads it; it is never stored.
   */
  bootIntents: string[] | null;
}

export const useBootHeaderSettings = (): BootHeaderSettings => {
  const {
    showBackButton,
    showCloseButton,
    headerStyle,
    layout,
    appearance,
    appearanceMode,
    transactionType,
    sessionType,
    preselectFirstPaymentMethod,
    preselectOnlyAvailableMethod,
    paymentMethodCount,
    withdrawalRequestsCount,
    replyArrived,
  } = useAppSelector((state: any) => {
    const initTheme = state.initTheme;
    const customizationSettings = initTheme?.theme_editor_settings?.customization_settings;
    const profileInfo = state.profileInfo || {};
    return {
      showBackButton: customizationSettings?.show_back_button,
      showCloseButton: customizationSettings?.show_close_button,
      headerStyle: customizationSettings?.header_style,
      layout: customizationSettings?.layout,
      appearance: customizationSettings?.appearance,
      appearanceMode: initTheme?.cashier_appearance_mode,
      transactionType: state.resultOfPay?.transaction_type || profileInfo.transaction_type,
      sessionType: state.resultOfPay?.session_type || profileInfo.session_type,
      preselectFirstPaymentMethod: customizationSettings?.preselect_first_payment_method,
      preselectOnlyAvailableMethod: customizationSettings?.preselect_only_available_method,
      paymentMethodCount: state.gatewaysList?.gateways?.length,
      withdrawalRequestsCount: state.withdrawalRequests?.withdrawalRequestsList?.length,
      // The session reply is the only writer of the stored session information.
      replyArrived: initTheme?.sessionInformation !== undefined,
    };
  }, shallowEqual);

  const bootIntents = replyArrived ? null : embeddedIntents;
  // The start-up transaction type comes from the first boot intent; without a
  // usable one the old customization transaction type is the fallback.
  const bootTransactionType =
    bootIntents && isKnownIntent(bootIntents[0])
      ? mapIntentToTransactionType(bootIntents[0])
      : embeddedTransactionType;
  const resolvedLayout = layout ?? embeddedLayout;
  const resolvedHeaderStyle = headerStyle ?? embeddedHeaderStyle;

  const headerButtons = resolveHeaderButtons({
    showBackButton: showBackButton ?? embeddedShowBackButton,
    showCloseButton: showCloseButton ?? embeddedShowCloseButton,
    layout: resolvedLayout,
    headerStyle: resolvedHeaderStyle,
  });

  return {
    ...headerButtons,
    supportsTabs:
      isPanoramicTheme &&
      supportsTabsHeader({ layout: resolvedLayout, headerStyle: resolvedHeaderStyle }),
    headerStyle: resolvedHeaderStyle,
    layout: resolvedLayout,
    appearanceMode:
      appearanceMode ??
      themeAppearanceMode(appearance ?? embeddedAppearance ?? 'system', locationCashierTheme),
    transactionType: transactionType ?? bootTransactionType ?? '',
    sessionType: sessionType ?? embeddedSessionType,
    isPreselectedPM: isPreselectedPaymentMethod({
      layout: resolvedLayout,
      preselectFirstPaymentMethod:
        preselectFirstPaymentMethod ?? embeddedPreselectFirstPaymentMethod,
      preselectOnlyAvailableMethod:
        preselectOnlyAvailableMethod ?? embeddedPreselectOnlyAvailableMethod,
      paymentMethodCount,
    }),
    withPending:
      (transactionType ?? bootTransactionType) === 'pay-in' && (withdrawalRequestsCount ?? 0) > 0,
    bootIntents,
  };
};
